// =========================================================
// El Faro · capa de datos real (Supabase)
//
// Mapea las tablas faro_* al modelo que usa la pantalla.
// La seguridad real vive en las políticas RLS: aquí solo
// pedimos datos, nunca decidimos permisos.
// =========================================================
import { supabase } from '../integrations/supabase/client';

const pad = (n) => String(n).padStart(2, '0');

// ---------- mapeos ----------
export function mapOpp(r, sourcesById = {}) {
  const src = sourcesById[r.source_id] || {};
  return {
    id: r.id,
    org: r.organization_id || 'all',
    kind: r.kind,
    destination: r.destination === 'ambos' ? 'proyecto' : r.destination,
    status: r.status === 'review' ? 'new' : r.status,
    title: r.title,
    funder: r.funder || src.name || '—',
    funderShort: r.funder_short,
    scope: r.scope || '—',
    summary: r.summary || '',
    // Sin dotación publicada => null (nunca 0 €, que confunde)
    amount: r.amount == null || Number(r.amount) <= 0 ? null : Number(r.amount),
    coFunding: r.co_funding,
    published: r.published_on,
    // Cuando la fecha viene del correo (y no del texto) la marcamos aproximada
    publishedApprox: !!(r.published_on && r.mail_received_at
      && String(r.mail_received_at).slice(0, 10) === String(r.published_on).slice(0, 10)),
    deadline: r.deadline,
    requirements: r.requirements || [],
    tags: r.tags || [],
    matched: r.matched || [],
    unmatched: r.unmatched || [],
    score: r.score ?? 0,
    excerpt: r.source_excerpt || '',
    url: r.source_url,
    sourceName: src.name || 'Fuente',
    sourceKind: src.kind === 'mail' ? 'mail' : 'web',
    mailSubject: r.mail_subject || null,
    mailFrom: r.mail_from || null,
    mailReceivedAt: r.mail_received_at || null,
    mailLink: r.mail_message_id ? `https://mail.google.com/mail/u/0/#all/${r.mail_message_id}` : null,
    detectedAt: r.detected_at,
    bidId: r.bid_id || null,
    sentAt: r.sent_at || null,
    documents: Array.isArray(r.documents) ? r.documents : [],
    documentsFetchedAt: r.documents_fetched_at || null,
  };

}


export function mapSource(r) {
  return {
    id: r.id,
    kind: r.kind === 'mail' ? 'mail' : 'web',
    rawKind: r.kind,
    name: r.name,
    url: r.url,
    org: r.organization_id || 'all',
    actorType: r.actor_type,
    level: r.level,
    topic: r.topic,
    freq: r.frequency,
    status: r.status,
    priority: r.priority,
    note: r.note,
    senders: (r.senders || []).length ? r.senders : null,
    mailQuery: r.mail_query || '',
    mailLabel: r.mail_label || '',
    lastMail: r.cursor_at || null,
    lastRun: r.last_run_at,
    lastError: r.last_error,
    found: r.found_total ?? 0,
    foundLast: r.found_last ?? 0,
  };
}

export function mapCriteria(r) {
  const dests = r?.destinations || ['evento', 'proyecto'];
  return {
    id: r?.id || null,
    org: r?.organization_id || 'all',
    themes: r?.topics || [],
    eventKeywords: r?.event_keywords || [],
    minAmount: Number(r?.min_amount ?? 0),
    minDays: r?.min_days ?? 10,
    minScore: r?.min_score ?? 50,
    scopes: r?.scopes || [],
    exclude: r?.exclude_keywords || [],
    destinations: { evento: dests.includes('evento'), proyecto: dests.includes('proyecto') },
  };
}

export function mapAlerts(r) {
  return {
    id: r?.id || null,
    enabled: r?.enabled ?? true,
    hour: `${pad(r?.send_hour ?? 9)}:00`,
    include: r?.include || { new: true, closingSoon: true, interesting: true, discarded: false },
    recipients: (r?.recipients || []).map((mail) => ({ mail, org: r?.organization_id || 'all' })),
    weekdaysOnly: true,
  };
}

// ---------- carga ----------
export async function loadFaro() {
  const [orgsRes, srcRes, oppRes, critRes, alertRes, runRes, schedRes] = await Promise.all([
    supabase.from('organizations').select('*').eq('active', true).order('name'),
    supabase.from('faro_sources').select('*').order('priority', { ascending: false }),
    supabase.from('faro_opportunities').select('*').order('detected_at', { ascending: false }).limit(500),
    supabase.from('faro_criteria').select('*'),
    supabase.from('faro_alerts').select('*'),
    supabase.from('faro_runs').select('*').order('started_at', { ascending: false }).limit(10),
    supabase.from('faro_schedule').select('*').eq('id', true).maybeSingle(),
  ]);

  const sources = (srcRes.data || []).map(mapSource);
  const sourcesById = {};
  (srcRes.data || []).forEach((s) => { sourcesById[s.id] = s; });

  const orgs = [
    { id: 'all', name: 'Todas las organizaciones', short: 'Todas' },
    ...(orgsRes.data || []).map((o) => ({ id: o.id, name: o.name, short: o.short_name || o.name, hue: o.hue })),
  ];

  const runs = runRes.data || [];
  const last = runs[0] || null;

  return {
    orgs,
    sources,
    opportunities: (oppRes.data || []).map((r) => mapOpp(r, sourcesById)),
    criteriaRows: (critRes.data || []).map(mapCriteria),
    criteria: mapCriteria((critRes.data || []).find((c) => !c.organization_id) || (critRes.data || [])[0]),
    alertsRows: alertRes.data || [],
    alerts: mapAlerts((alertRes.data || []).find((a) => !a.organization_id) || (alertRes.data || [])[0]),
    schedule: schedRes.data || null,
    runs,
    run: last
      ? {
          lastRunAt: last.finished_at || last.started_at,
          status: last.status,
          pagesScanned: last.pages_scraped ?? 0,
          mailsScanned: last.mails_read ?? 0,
          found: last.found ?? 0,
          discarded: last.discarded ?? 0,
          duplicates: last.duplicates ?? 0,
          errors: last.errors || [],
        }
      : { lastRunAt: null, pagesScanned: 0, mailsScanned: 0, found: 0, discarded: 0, duplicates: 0, errors: [] },
  };
}

// ---------- mutaciones ----------
export async function setOppStatus(ids, status, userId) {
  const { error } = await supabase
    .from('faro_opportunities')
    .update({ status, reviewed_by: userId || null, reviewed_at: new Date().toISOString() })
    .in('id', ids);
  if (error) throw error;
}

export async function createFaroSource(payload, userId) {
  const { data, error } = await supabase.from('faro_sources').insert({
    kind: payload.kind === 'mail' ? 'mail' : 'web',
    name: payload.name,
    url: payload.url,
    organization_id: payload.org === 'all' ? null : payload.org,
    frequency: payload.freq || 'daily',
    status: payload.status || 'active',
    actor_type: payload.actorType || null,
    level: payload.level || null,
    topic: payload.topic || null,
    note: payload.note || null,
    senders: payload.senders || [],
    mail_query: payload.mailQuery || null,
    mail_label: payload.mailLabel || null,
    created_by: userId || null,
  }).select('*').single();
  if (error) throw error;
  return mapSource(data);
}

export async function updateFaroSource(id, payload) {
  const patch = {};
  if (payload.name != null) patch.name = payload.name;
  if (payload.url != null) patch.url = payload.url;
  if (payload.org != null) patch.organization_id = payload.org === 'all' ? null : payload.org;
  if (payload.freq != null) patch.frequency = payload.freq;
  if (payload.status != null) patch.status = payload.status;
  if (payload.note !== undefined) patch.note = payload.note || null;
  if (payload.kind != null) patch.kind = payload.kind === 'mail' ? 'mail' : 'web';
  if (payload.senders !== undefined) patch.senders = payload.senders || [];
  if (payload.mailQuery !== undefined) patch.mail_query = payload.mailQuery || null;
  if (payload.mailLabel !== undefined) patch.mail_label = payload.mailLabel || null;
  const { data, error } = await supabase.from('faro_sources')
    .update(patch).eq('id', id).select('*').single();
  if (error) throw error;
  return mapSource(data);
}

export async function toggleFaroSource(id, status) {
  const { data, error } = await supabase.from('faro_sources')
    .update({ status }).eq('id', id).select('*').single();
  if (error) throw error;
  return mapSource(data);
}

export async function deleteFaroSource(id) {
  const { error } = await supabase.from('faro_sources').delete().eq('id', id);
  if (error) throw error;
}

export async function saveFaroCriteria(criteria, userId) {
  const payload = {
    topics: criteria.themes || [],
    event_keywords: criteria.eventKeywords || [],
    min_amount: criteria.minAmount ?? 0,
    min_days: criteria.minDays ?? 10,
    min_score: criteria.minScore ?? 50,
    scopes: criteria.scopes || [],
    exclude_keywords: criteria.exclude || [],
    destinations: Object.entries(criteria.destinations || {}).filter(([, v]) => v).map(([k]) => k),
    updated_by: userId || null,
  };
  if (criteria.id) {
    const { error } = await supabase.from('faro_criteria').update(payload).eq('id', criteria.id);
    if (error) throw error;
    return criteria.id;
  }
  const { data, error } = await supabase.from('faro_criteria')
    .insert({ ...payload, organization_id: criteria.org === 'all' ? null : criteria.org })
    .select('id').single();
  if (error) throw error;
  return data.id;
}

export async function saveFaroAlerts(alerts, userId) {
  const payload = {
    enabled: !!alerts.enabled,
    send_hour: Number(String(alerts.hour || '09:00').slice(0, 2)),
    include: alerts.include || {},
    recipients: (alerts.recipients || []).map((r) => r.mail).filter(Boolean),
    updated_by: userId || null,
  };
  if (alerts.id) {
    const { error } = await supabase.from('faro_alerts').update(payload).eq('id', alerts.id);
    if (error) throw error;
    return alerts.id;
  }
  const { data, error } = await supabase.from('faro_alerts').insert(payload).select('id').single();
  if (error) throw error;
  return data.id;
}

// Panel de automatización (solo Admin por RLS)
export async function saveFaroSchedule(patch, userId) {
  const { data, error } = await supabase.from('faro_schedule')
    .update({ ...patch, updated_by: userId || null })
    .eq('id', true)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// Lanza un rastreo manual (la función valida que quien llama sea Admin)
export async function runFaroScan({ sourceId = null } = {}) {
  const { data, error } = await supabase.functions.invoke('faro-scan', {
    body: { trigger: 'manual', force: true, ...(sourceId ? { source_id: sourceId } : {}) },
  });
  if (error) throw error;
  return data;
}

// ---------- puente a la fase de análisis ----------
// Busca en la página oficial de la convocatoria los documentos descargables
// (bases, anexos, pliegos) para no tener que bajarlos a mano.
export async function fetchOppDocs(url, opportunityId) {
  const { data, error } = await supabase.functions.invoke('faro-fetch-docs', {
    body: { url, ...(opportunityId ? { opportunity_id: opportunityId } : {}) },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}


// Crea la licitación a partir de la convocatoria y guarda los documentos
// seleccionados. Devuelve { bid_id, documents, warnings }.
export async function sendOppToBid({ opportunityId, lot, docUrls = [] }) {
  const { data, error } = await supabase.functions.invoke('faro-to-bid', {
    body: { opportunity_id: opportunityId, ...(lot ? { lot } : {}), doc_urls: docUrls },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}
